import { create } from "zustand";

import type {
  Cell,
  CrosswordGrid,
  PositionedWord,
  Word,
} from "@edclub/crosswords-engine";

import type { MoodType } from "@/components/AnimatedGiraffe";
import { Sfx, playAudio } from "@/utils/sfx";

import { createSelectors } from "./create-selectors";

type Options = {
  hints: number;
  initialRevealedLetterCount: number;
};

export type ActiveCell = {
  row: number;
  col: number;
};

export type GridCell = ActiveCell & {
  letter?: string;
};

export type StateUpdate = {
  crossword: CrosswordGrid | null;
  options: Options;
  words: Word[];
  revealedLetters: { [wordId: string]: number[] };
  useImagesIfAvailable: boolean;
};

export type CrosswordStoreProps = {
  words: Word[];
  crossword: CrosswordGrid | null;
  options: Options;
  activeWordId: number | null;
  activeCell: Cell | null;
  revealedCells: { [cellKey: string]: boolean };
  positionedWords: PositionedWord[];
  giraffeMood: MoodType;
  useImagesIfAvailable: boolean;
  imageSize: "compact" | "full";
  isChecked: boolean;
  initialHints: number;
  initialRevealedCells: { [cellKey: string]: boolean };
  setImageSize: (size: "compact" | "full") => void;
  addWord: (word: Omit<Word, "id">) => void;
  removeWord: (id: Word["id"]) => void;
  setActiveCell: (cell: Cell | null) => void;
  setActiveWord: (id: number | null) => void;
  setStore: (state: StateUpdate) => void;
  updateCellUserAnswer: (row: number, col: number, newValue: string) => void;
  revealHint: () => void;
  setGiraffeMood: (mood: MoodType) => void;
  checkCrossword: () => void;
  isCrosswordCompleted: () => boolean;
  getSummaryReport: () => SummaryReport;
};

export type SummaryReport = {
  hints: {
    initialHints: number;
    hintsRemaining: number;
    hintsUsed: number;
    hintCellsRevealed: number;
  };
  words: {
    total: number;
    fullyCorrect: number;
    notEntered: number;
    withMistakes: number;
  };
  cells: {
    totalPlayable: number;
    defaultRevealed: number;
    toFill: number;
    filledByUser: number;
    correctByUser: number;
    incorrectByUser: number;
    filledIncludingHints: number;
    correctIncludingHints: number;
    incorrectIncludingHints: number;
    completionPercentUser: number; // 0..100
    completionPercentOverall: number; // 0..100
  };
  performance: {
    score: number; // 0..100
    grade: string; // A/B/C/D/F or "N/A"
  };

  entries: {
    question: string;
    userAnswer: string;
    correctAnser: string;
  }[];
};

const getCellAt = (
  crossword: CrosswordGrid,
  targetRow: number,
  targetCol: number,
) => {
  if (
    crossword &&
    crossword.grid &&
    crossword.grid[targetRow] &&
    crossword.grid[targetRow][targetCol]
  ) {
    return crossword.grid[targetRow][targetCol];
  }

  return null;
};

const useCrosswordStoreBase = create<CrosswordStoreProps>((set, get) => ({
  words: [],
  useImagesIfAvailable: false,
  crossword: null,
  options: { hints: 0, initialRevealedLetterCount: 0 },
  activeWordId: null,
  activeCell: null,
  revealedCells: {},
  positionedWords: [],
  giraffeMood: "happy",
  imageSize: "compact",
  isChecked: false,
  initialHints: 0,
  initialRevealedCells: {},

  setImageSize(size) {
    set({ imageSize: size });
  },

  checkCrossword() {
    const { crossword } = get();

    if (!crossword) {
      return;
    }

    const newGrid = crossword.grid.map((row) =>
      row.map((cell) => {
        if (cell.letter !== "") {
          if (cell.userAnswer && cell.userAnswer.length > 0) {
            const isCorrect =
              cell?.userAnswer.toLowerCase() === cell.letter.toLowerCase();

            return { ...cell, status: isCorrect ? "correct" : "incorrect" };
          }

          if (!cell.userAnswer) {
            return { ...cell, status: "incorrect" };
          }
        }

        // For cells with no letter or no userAnswer, leave them unchanged.
        return cell;
      }),
    );

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    set((state) => ({
      crossword: {
        ...state.crossword!,
        grid: newGrid,
      },
      isChecked: true,
    }));
  },

  setGiraffeMood(mood) {
    set({ giraffeMood: mood });
  },

  revealHint() {
    const currentHints = get().options.hints;
    const crossword = get().crossword;

    if (currentHints > 0 && crossword) {
      const crossword = get().crossword;

      const grid = crossword!.grid;

      const candidateCells: { row: number; col: number }[] = [];

      for (let row = 0; row < grid.length; row++) {
        for (let col = 0; col < grid[row].length; col++) {
          const cell = grid[row][col];

          if (cell.letter !== "" && !cell.revealed && !cell.userAnswer) {
            candidateCells.push({ row, col });
          }
        }
      }

      if (candidateCells.length === 0) {
        return;
      }

      const randomIndex = Math.floor(Math.random() * candidateCells.length);
      const chosenCell = candidateCells[randomIndex];

      const newGrid = grid.map((rowCells, rowIndex) => {
        if (rowIndex !== chosenCell.row) {
          return rowCells;
        }

        return rowCells.map((cell, colIndex) => {
          if (colIndex !== chosenCell.col) {
            return cell;
          }

          return {
            ...cell,
            revealed: true,
            userAnswer: cell.letter.toUpperCase(),
          };
        });
      });

      playAudio(Sfx.HintSound);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      set((prev) => ({
        crossword: {
          ...prev.crossword,
          grid: newGrid,
        },
        options: {
          ...prev.options,
          hints: currentHints - 1,
        },
      }));
    }
  },

  updateCellUserAnswer: (row, col, newValue) => {
    const activeWord = get().positionedWords.find(
      (w) => w.id === get().activeWordId,
    );
    const isAcross = activeWord?.direction === "across";
    const activeWordLength = activeWord!.answer.length;

    if (!isAcross) {
      // word starting point - current row on the grid === word's length
      // when they match we have reached the last cell.
      const actualRowIndex = row - activeWord!.row;
      const finishedWord = actualRowIndex === activeWordLength - 1;

      if (finishedWord) {
        void setTimeout(() => {
          const lastCell = getCellAt(get().crossword!, row, col);
          get().setActiveCell(lastCell);
        });
      } else {
        const newCell = getCellAt(get().crossword!, row + 1, col);
        get().setActiveCell(newCell);
      }
    }

    if (isAcross) {
      // word starting point - current column on the grid === word's length
      // when they match we have reached the last cell.
      const actualColIndex = col - activeWord!.col;
      const finishedWord = actualColIndex === activeWordLength - 1;

      if (finishedWord) {
        void setTimeout(() => {
          const lastCell = getCellAt(get().crossword!, row, col);
          get().setActiveCell(lastCell);
        });
        // get().setActiveWord()
      } else {
        const newCell = getCellAt(get().crossword!, row, col + 1);
        get().setActiveCell(newCell);
      }
    }

    set((state) => {
      if (!state.crossword) {
        return {};
      }

      const newGrid = state.crossword.grid.map((rowCells, r) =>
        rowCells.map((cell, c) => {
          if (r === row && c === col) {
            return { ...cell, userAnswer: newValue };
          }

          return cell;
        }),
      );

      return {
        crossword: {
          ...state.crossword,
          grid: newGrid,
        },
      };
    });
  },

  addWord(word) {
    const newWord = { id: get().words.length + 1, ...word };
    const newWords = [...get().words, newWord];
    set({ words: newWords });
  },

  removeWord(id) {
    set((state) => {
      const filtered = state.words.filter((w) => w.id !== id);

      return { words: filtered };
    });
  },

  setActiveCell(cell) {
    if (get().isChecked) {
      return;
    }

    playAudio(Sfx.HoverSound);
    set({ activeCell: cell });
  },

  setActiveWord(id) {
    set({ activeWordId: id });
  },

  setStore(state) {
    const { crossword, options, words, useImagesIfAvailable = false } = state;

    if (!crossword) {
      return;
    }

    const initialRevealedCells: { [cellKey: string]: boolean } = {};

    for (let r = 0; r < crossword.grid.length; r++) {
      for (let c = 0; c < crossword.grid[r].length; c++) {
        const cell = crossword.grid[r][c];

        if (cell.letter !== "" && cell.revealed) {
          initialRevealedCells[`${r}-${c}`] = true;
        }
      }
    }

    set({
      positionedWords: [
        ...Object.values(crossword.across),
        ...Object.values(crossword.down),
      ],
      options,
      words,
      crossword,
      useImagesIfAvailable,
      initialHints: options.hints,
      initialRevealedCells,
    });
  },

  isCrosswordCompleted() {
    const { crossword } = get();

    if (!crossword) {
      return true;
    }

    for (let row = 0; row < crossword.grid.length; row++) {
      for (let col = 0; col < crossword.grid[row].length; col++) {
        const cell = crossword.grid[row][col];

        if (cell.letter !== "" && !cell.userAnswer && !cell.revealed) {
          return false;
        }
      }
    }

    return true;
  },

  getSummaryReport() {
    const state = get();
    const crossword = state.crossword;

    if (!crossword) {
      return {
        hints: {
          initialHints: state.initialHints,
          hintsRemaining: state.options.hints,
          hintsUsed: Math.max(0, state.initialHints - state.options.hints),
          hintCellsRevealed: 0,
        },
        words: {
          total: 0,
          fullyCorrect: 0,
          notEntered: 0,
          withMistakes: 0,
        },
        cells: {
          totalPlayable: 0,
          defaultRevealed: 0,
          toFill: 0,
          filledByUser: 0,
          correctByUser: 0,
          incorrectByUser: 0,
          filledIncludingHints: 0,
          correctIncludingHints: 0,
          incorrectIncludingHints: 0,
          completionPercentUser: 0,
          completionPercentOverall: 0,
        },
        performance: {
          score: 0,
          grade: "N/A",
        },
        entries: [],
      };
    }

    const defaultRevealedSet = state.initialRevealedCells;

    let totalPlayable = 0;
    let defaultRevealed = 0;
    let filledByUser = 0;
    let correctByUser = 0;
    let incorrectByUser = 0;
    let hintCellsRevealed = 0;
    let filledIncludingHints = 0;
    let correctIncludingHints = 0;

    for (let r = 0; r < crossword.grid.length; r++) {
      for (let c = 0; c < crossword.grid[r].length; c++) {
        const cell = crossword.grid[r][c];

        if (cell.letter === "") {
          continue;
        }

        totalPlayable++;

        const coordKey = `${r}-${c}`;
        const isDefaultRevealed = Boolean(defaultRevealedSet[coordKey]);
        const isCurrentlyRevealed = Boolean(cell.revealed);
        const isHintRevealed = isCurrentlyRevealed && !isDefaultRevealed;
        const userAnswer = (cell.userAnswer || "").toUpperCase();
        const solution = (cell.letter || "").toUpperCase();

        if (isDefaultRevealed) {
          defaultRevealed++;
          filledIncludingHints++;
          correctIncludingHints++;
          continue;
        }

        if (isHintRevealed) {
          hintCellsRevealed++;
          filledIncludingHints++;
          correctIncludingHints++;
        }

        if (userAnswer.length > 0) {
          filledByUser++;
          filledIncludingHints++;
          const isCorrect = userAnswer === solution;

          if (isCorrect) {
            correctByUser++;
            correctIncludingHints++;
          } else {
            incorrectByUser++;
          }
        }
      }
    }

    const toFill = Math.max(0, totalPlayable - defaultRevealed);
    const incorrectIncludingHints = incorrectByUser; // only user can be incorrect
    const filledOverallExcludingDefaults = Math.max(
      0,
      filledIncludingHints - defaultRevealed,
    );
    const completionPercentUser =
      toFill === 0
        ? 100
        : Math.min(100, Math.round((filledByUser / toFill) * 100));
    const completionPercentOverall =
      toFill === 0
        ? 100
        : Math.min(
            100,
            Math.round((filledOverallExcludingDefaults / toFill) * 100),
          );

    const words = state.positionedWords;
    let fullyCorrect = 0;
    let notEntered = 0;
    let withMistakes = 0;

    for (const w of words) {
      const length = w.answer.length;
      let wordPlayable = 0;
      let wordCorrect = 0;
      let wordFilledByUser = 0;
      let wordFilledAny = 0;

      for (let i = 0; i < length; i++) {
        const r = w.direction === "down" ? w.row + i : w.row;
        const c = w.direction === "across" ? w.col + i : w.col;
        const cell = crossword.grid[r]?.[c];

        if (!cell || cell.letter === "") {
          continue;
        }

        wordPlayable++;

        const coordKey = `${r}-${c}`;
        const isDefaultRevealed = Boolean(defaultRevealedSet[coordKey]);
        const isCurrentlyRevealed = Boolean(cell.revealed);
        const isHintRevealed = isCurrentlyRevealed && !isDefaultRevealed;
        const userAnswer = (cell.userAnswer || "").toUpperCase();
        const solution = (cell.letter || "").toUpperCase();

        const isCorrect =
          isDefaultRevealed || isHintRevealed || userAnswer === solution;

        if (isCorrect) {
          wordCorrect++;
        }

        const userFilled =
          userAnswer.length > 0 && !isDefaultRevealed && !isHintRevealed;

        if (userFilled) {
          wordFilledByUser++;
        }

        const anyFilled =
          isDefaultRevealed || isHintRevealed || userAnswer.length > 0;

        if (anyFilled) {
          wordFilledAny++;
        }
      }

      if (wordPlayable === 0) {
        continue;
      }

      if (wordCorrect === wordPlayable) {
        fullyCorrect++;
      } else if (wordFilledByUser === 0) {
        notEntered++;
      } else if (wordFilledAny > 0 && wordCorrect < wordPlayable) {
        withMistakes++;
      }
    }

    // Build per-word entries: { question, userAnswer, correctAnser }
    const entries = words.map((w) => {
      const length = w.answer.length;
      const letters: string[] = [];

      for (let i = 0; i < length; i++) {
        const r = w.direction === "down" ? w.row + i : w.row;
        const c = w.direction === "across" ? w.col + i : w.col;
        const cell = crossword.grid[r]?.[c];

        if (!cell || cell.letter === "") {
          letters.push("");
          continue;
        }

        const coordKey = `${r}-${c}`;
        const isDefaultRevealed = Boolean(defaultRevealedSet[coordKey]);
        const isCurrentlyRevealed = Boolean(cell.revealed);
        const isHintRevealed = isCurrentlyRevealed && !isDefaultRevealed;
        const userChar = (cell.userAnswer || "").toUpperCase();
        const solutionChar = (cell.letter || "").toUpperCase();

        const visibleChar =
          isDefaultRevealed || isHintRevealed ? solutionChar : userChar;
        letters.push(visibleChar);
      }

      return {
        question: w.clue,
        userAnswer: letters.join(""),
        correctAnser: (w.answer || "").toUpperCase(),
      };
    });

    // performance score and grade
    const completionUserRatio = completionPercentUser / 100;
    const accuracyRatio =
      filledByUser > 0 ? correctByUser / Math.max(1, filledByUser) : 0;
    const wordsCorrectRatio =
      words.length > 0 ? fullyCorrect / words.length : 0;
    const mistakePenaltyRatio = toFill === 0 ? 0 : incorrectByUser / toFill;
    const hintsUsed = Math.max(0, state.initialHints - state.options.hints);

    const baseScore01 = Math.max(
      0,
      Math.min(
        1,
        0.6 * accuracyRatio +
          0.3 * completionUserRatio +
          0.1 * wordsCorrectRatio -
          0.2 * mistakePenaltyRatio,
      ),
    );
    const baseScore = Math.round(baseScore01 * 100);
    const HINT_PENALTY_POINTS = 3; // points deducted per used hint
    const performanceScore = Math.max(
      0,
      Math.min(100, baseScore - hintsUsed * HINT_PENALTY_POINTS),
    );
    // Not really used right now
    const performanceGrade =
      performanceScore >= 90
        ? "A"
        : performanceScore >= 80
          ? "B"
          : performanceScore >= 70
            ? "C"
            : performanceScore >= 60
              ? "D"
              : "F";

    return {
      hints: {
        initialHints: state.initialHints,
        hintsRemaining: state.options.hints,
        hintsUsed,
        hintCellsRevealed,
      },
      words: {
        total: words.length,
        fullyCorrect,
        notEntered,
        withMistakes,
      },
      cells: {
        totalPlayable,
        defaultRevealed,
        toFill,
        filledByUser,
        correctByUser,
        incorrectByUser,
        filledIncludingHints,
        correctIncludingHints,
        incorrectIncludingHints,
        completionPercentUser,
        completionPercentOverall,
      },
      performance: {
        score: performanceScore,
        grade: performanceGrade,
      },
      entries,
    };
  },
}));

export const useCrosswordStore = createSelectors(useCrosswordStoreBase);
